/**
 * Hook for token replacement in text strings
 * Migrated from front/src/hooks/useTokenReplacement.js
 */

import { useCallback } from 'react';
import Mustache from 'mustache';
import { useSelector } from 'react-redux';
import { selectOutlook } from '../../store/slices/outlookSlice';
import { selectCurrentEntity } from '../../store/slices/navigationSlice';
import {
  formattedEntity,
  formattedTheEntity,
  formatDomain,
  formatDomainWithArticle,
} from '../../utils/entityHelpers';
import { OVERSEAS_TERRITORIES, REFERENCE_PERIODS } from '../../config/settings';

// Disable Mustache HTML escaping
Mustache.escape = text => text;

/**
 * Hook for replacing tokens in text strings using Mustache templating
 * @param {Object} options - Hook options
 * @param {string} options.entityType - Type of entity ('commune' or 'epci')
 * @param {string} options.icu - Îlot de chaleur urbain text
 * @returns {Function} Token replacement function
 */
const useTokenReplacement = ({
  entityType = 'commune',
  icu,
} = {}) => {
  const { value, rwl, gwl } = useSelector(selectOutlook);
  const entity = useSelector(selectCurrentEntity);

  /**
   * Replace tokens in text string
   * @param {string} text - Text containing tokens to replace
   * @param {Object} extraTokens - Additional tokens to include
   * @returns {string} Text with tokens replaced
   */
  const replaceTokens = useCallback(
    (text = '', extraTokens = {}) => {
      // Format domain names
      const domainName = formatDomain(entity?.domaine);
      const domainWithArticle = formatDomainWithArticle(entity?.domaine);

      // Reference period based on domain
      const isOverseas = OVERSEAS_TERRITORIES.isOverseas(entity?.domaine);
      const refPeriod = isOverseas ? REFERENCE_PERIODS.OVERSEAS : REFERENCE_PERIODS.HEXAGONAL;

      return Mustache.render(
        text,
        {
          // commune || EPCI
          entité: formattedEntity[entityType],
          entity: formattedEntity[entityType],

          // la commune || l'EPCI
          l_entité: formattedTheEntity[entityType],
          the_entity: formattedTheEntity[entityType],

          // Domain tokens
          domaine: domainName,
          le_domaine: domainWithArticle,
          the_domain: domainWithArticle,

          // Reference period
          ref_period: refPeriod,

          // Outlook
          outlook: value,
          horizon: value,

          // Warming Levels string as: +X°C
          rwl,
          RWL: rwl,
          gwl,
          GWL: gwl,

          // "Îlot de chaleur urbain"
          icu,

          ...extraTokens,
        },
      );
    },
    [entityType, value, rwl, gwl, icu, entity?.domaine],
  );

  return replaceTokens;
};

export default useTokenReplacement;
